/**
 * The Update panel's Time section (#114): minutes, the coverage domain (Phone / IT / MACD) and billable. Kept in one
 * component. Round 13: the domain is pre-picked from GET /cases/{id}/time-prompt (`defaults`), and each domain shows
 * its coverage basis. `billable: null` uses a known coverage default, but an unknown lookup stays visibly undecided
 * and must be chosen before save (#377).
 */
import { Input, Picker, parseDuration, SearchableSelect, type SearchableSelectOption, Textarea } from '@patchbay/ui'
import type * as React from 'react'
import type { CoverageDomain, OwnerPerson } from '../../lib/api/generated'
import type { TimeDefaultsOut } from '../case-view/api'
import { CoverageBasisNote } from './coverage-basis-note'

export interface TimeValue {
  minutes: string
  domain: CoverageDomain | null
  billable: boolean | null
  /** Empty/self, `user:<uuid>`, or `agent:<desk id>` for #256 delegated time. */
  owner: string
  /** #1186: the note stored on this time entry and carried to Desk/Books/close-out. */
  note?: string
}

export const TIME_DOMAINS: { id: CoverageDomain; label: string }[] = [
  { id: 'phone', label: 'Phone' },
  { id: 'it', label: 'IT' },
  { id: 'macd', label: 'MACD' },
]

export function minutesOf(v: TimeValue): number {
  return parseDuration(v.minutes) ?? 0
}

export function durationEcho(raw: string): string | null {
  const minutes = parseDuration(raw)
  if (!minutes) return null
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} m`
}

/** The domain the entry will carry: the person's pick, else the case's round-13 pre-pick. */
export function effectiveDomain(v: TimeValue, defaults?: TimeDefaultsOut | null): CoverageDomain | null {
  return v.domain ?? (defaults?.domain as CoverageDomain | undefined) ?? null
}

export function TimeSection({
  value,
  onChange,
  inputRef,
  defaults,
  people = [],
  meId,
  canDelegate = false,
}: {
  value: TimeValue
  onChange: (v: TimeValue) => void
  inputRef?: React.Ref<HTMLInputElement>
  /** Round 13 time-prompt defaults; undefined while loading or unreachable (then the API decides on save). */
  defaults?: TimeDefaultsOut | null
  people?: OwnerPerson[]
  meId?: string
  canDelegate?: boolean
}) {
  const set = (patch: Partial<TimeValue>) => onChange({ ...value, ...patch })
  const picked = effectiveDomain(value, defaults)
  const option = defaults?.domains.find((d) => d.domain === picked)
  const coverageKnown = Boolean(option && option.decision !== 'unknown')
  // This is the Desk time entry's billable flag, not the case coverage decision. Keep the labels distinct so the
  // close dialog never presents two controls that both appear to answer "Coverage" (#802/#526).
  const defaultLabel = coverageKnown
    ? option?.billable_default
      ? 'Billable (per the agreement)'
      : 'Covered by the agreement (not billed)'
    : 'Choose whether to bill this time'
  const self = people.find((person) => person.user_id === meId)
  const selfValue = meId ? `user:${meId}` : ''
  const timeOwner = value.owner || selfValue
  const ownerOptions: SearchableSelectOption[] = [
    ...(self ? [{ value: selfValue, label: `${self.name} (me)`, detail: self.email ?? undefined, group: 'Log time for' }] : []),
    ...(canDelegate
      ? people
          .filter((person) => person.user_id !== meId)
          .map((person) => ({
            value: person.user_id ? `user:${person.user_id}` : `agent:${person.desk_agent_id}`,
            label: person.name,
            detail: person.user_id ? (person.email ?? undefined) : 'Desk-only agent',
            group: 'Log time for',
          }))
      : []),
  ]
  const echo = durationEcho(value.minutes)
  const bareSmall = /^\d{1,2}$/.test(value.minutes.trim()) && Number(value.minutes) <= 12 && Number(value.minutes) > 0
  const field = 'grid min-w-0 content-start gap-1.5 text-xs font-medium text-muted-foreground'
  return (
    <div data-testid="time-section" className="grid grid-cols-1 gap-x-3 gap-y-3 sm:grid-cols-2">
      <div className={`${field} ${ownerOptions.length ? '' : 'sm:col-span-2'}`}>
        <label htmlFor="time-minutes">Time</label>
        <div className="flex h-9 items-center gap-1.5">
          <Input
            id="time-minutes"
            ref={inputRef}
            aria-label="Minutes"
            inputMode="text"
            className="h-9 min-w-0 flex-1"
            placeholder="1:30 or 90m"
            value={value.minutes}
            onChange={(e) => set({ minutes: e.currentTarget.value })}
          />
          {echo ? <span className="whitespace-nowrap font-normal">= {echo}</span> : null}
        </div>
      </div>
      {ownerOptions.length ? (
        <div className={field}>
          <span>Log time for</span>
          <SearchableSelect
            ariaLabel="Log time for"
            className="h-9 w-full"
            value={timeOwner}
            onChange={(owner) => set({ owner })}
            options={ownerOptions}
            placeholder="Log time for…"
          />
        </div>
      ) : null}
      <fieldset aria-label="Time domain" className={field}>
        <span aria-hidden>Coverage</span>
        <div className="flex h-9 w-full min-w-0 overflow-hidden rounded-md border border-border">
          {TIME_DOMAINS.map((d) => (
            <button
              key={d.id}
              type="button"
              aria-pressed={picked === d.id}
              className={`h-full min-w-0 flex-1 px-2 text-sm ${picked === d.id ? 'bg-primary text-primary-foreground' : 'bg-card text-foreground hover:bg-muted'}`}
              onClick={() => set({ domain: d.id, billable: null })}
            >
              {d.label}
            </button>
          ))}
        </div>
        {option ? <CoverageBasisNote basis={option.basis} detail={option.basis_detail} /> : null}
      </fieldset>
      <div className={field}>
        <span>Billing</span>
        <Picker
          aria-label="Billable"
          className="h-9 w-full min-w-0 max-w-full truncate rounded-md border border-input bg-card px-2 text-sm text-foreground"
          value={value.billable === null ? (coverageKnown ? 'default' : '') : value.billable ? 'yes' : 'no'}
          onChange={(e) => {
            const v = e.currentTarget.value
            set({ billable: v === 'default' || v === '' ? null : v === 'yes' })
          }}
        >
          <option value={coverageKnown ? 'default' : ''}>{defaultLabel}</option>
          <option value="yes">Billable</option>
          <option value="no">Not billable</option>
        </Picker>
      </div>
      <label className={`${field} sm:col-span-2`}>
        Time entry note
        <Textarea
          aria-label="Time entry note"
          className="min-h-20 font-normal text-foreground"
          value={value.note ?? ''}
          onChange={(event) => set({ note: event.currentTarget.value })}
          placeholder="Work performed; this stays with the time entry"
        />
      </label>
      {bareSmall ? (
        <p className="text-xs text-warning sm:col-span-2">
          {value.minutes} means {value.minutes} minutes. For hours, enter {value.minutes}h.
        </p>
      ) : null}
    </div>
  )
}
